import { optionalCounterpartyEmailSchema } from "../schemas/counterparty.schema";
import {
    isLikelyCompanyName,
    nameWords,
    normalizeCompanyName,
} from "./company-name-match";
import {
    addressKey,
    indexOfTokenSequence,
    isSavedAddress,
    presentAddressParts,
    SavedCounterpartyAddress,
} from "./counterpartyAddressMatch";
import { matchablePhoneDigits } from "./phone";

export interface SavedCounterpartyContact {
    email: string | null;
    phoneNumber: string | null;
    title: string | null;
    firstName: string | null;
    middleName: string | null;
    lastName: string | null;
    suffix: string | null;
}

export interface SavedCounterpartyFields
    extends SavedCounterpartyContact, SavedCounterpartyAddress {
    name: string | null;
}

export interface SavedCounterpartyRecord {
    counterparty: SavedCounterpartyFields;
    contacts: SavedCounterpartyContact[];
    addresses: SavedCounterpartyAddress[];
}

export interface SavedCounterpartyDetails {
    name: string | null;
    emails: string[];
    phoneNumbers: string[];
    contactNames: string[];
    addresses: SavedCounterpartyAddress[];
}

export interface ExtractedCounterpartyParty {
    partyName: string | null;
    names: string[];
    emails: string[];
    phoneNumbers: string[];
    addresses: string[];
}

export interface CounterpartyDetailChanges {
    emails: string[];
    phoneNumbers: string[];
    contactNames: string[];
    addresses: string[];
}

export const EMPTY_COUNTERPARTY_DETAIL_CHANGES: CounterpartyDetailChanges = {
    emails: [],
    phoneNumbers: [],
    contactNames: [],
    addresses: [],
};

const MIN_PARTY_NAME_LENGTH = 4;

function presentParts(parts: Array<string | null | undefined>): string[] {
    return parts.flatMap((part) => {
        const trimmed = part?.trim();
        return trimmed ? [trimmed] : [];
    });
}

function isSamePartyName(candidate: string, savedName: string): boolean {
    const left = nameWords(candidate);
    const right = nameWords(savedName);
    if (left.length === 0 || right.length === 0) {
        return false;
    }
    if (left.join("") === right.join("")) {
        return true;
    }
    const isLeftShorter = left.length < right.length;
    const shorter = isLeftShorter ? left : right;
    const longer = isLeftShorter ? right : left;
    return (
        shorter.join("").length >= MIN_PARTY_NAME_LENGTH &&
        indexOfTokenSequence(longer, shorter) >= 0
    );
}

function emailKey(value: string): string | null {
    const parsed = optionalCounterpartyEmailSchema.safeParse(value);
    if (!parsed.success || !parsed.data) {
        return null;
    }
    return parsed.data.toLowerCase();
}

function contactName(contact: SavedCounterpartyContact): string | null {
    const name = presentParts([
        contact.title,
        contact.firstName,
        contact.middleName,
        contact.lastName,
        contact.suffix,
    ]).join(" ");
    return name || null;
}

function hasAddressParts(address: SavedCounterpartyAddress): boolean {
    return presentAddressParts(address).length > 0;
}

function valuesMissingFromSaved({
    extracted,
    savedKeys,
    toKey,
}: {
    extracted: string[];
    savedKeys: Set<string>;
    toKey: (value: string) => string | null;
}): string[] {
    const seen = new Set<string>();
    const missing: string[] = [];
    for (const value of extracted) {
        const trimmed = value.trim();
        const key = toKey(trimmed);
        if (!key || savedKeys.has(key) || seen.has(key)) {
            continue;
        }
        seen.add(key);
        missing.push(trimmed);
    }
    return missing;
}

function addressesMissingFromSaved({
    extracted,
    saved,
}: {
    extracted: string[];
    saved: SavedCounterpartyAddress[];
}): string[] {
    const seen = new Set<string>();
    const missing: string[] = [];
    for (const value of extracted) {
        const trimmed = value.trim();
        const key = addressKey(trimmed);
        if (!key || seen.has(key)) {
            continue;
        }
        if (
            saved.some((address) =>
                isSavedAddress({ extracted: trimmed, saved: address }),
            )
        ) {
            continue;
        }
        seen.add(key);
        missing.push(trimmed);
    }
    return missing;
}

export function savedCounterpartyDetails({
    counterparty,
    contacts: savedContacts,
    addresses: savedAddresses,
}: SavedCounterpartyRecord): SavedCounterpartyDetails {
    const contacts: SavedCounterpartyContact[] = [
        counterparty,
        ...savedContacts,
    ];
    const addresses: SavedCounterpartyAddress[] = [
        counterparty,
        ...savedAddresses,
    ];

    return {
        name: counterparty.name?.trim() || null,
        emails: contacts.flatMap((contact) =>
            contact.email ? [contact.email] : [],
        ),
        phoneNumbers: contacts.flatMap((contact) =>
            contact.phoneNumber ? [contact.phoneNumber] : [],
        ),
        contactNames: contacts.flatMap((contact) => {
            const name = contactName(contact);
            return name ? [name] : [];
        }),
        addresses: addresses.filter(hasAddressParts),
    };
}

export function personNamesNotOnRecord({
    names,
    savedNames,
}: {
    names: string[];
    savedNames: string[];
}): string[] {
    const seenContactNames = new Set<string>();
    const contactNames: string[] = [];
    for (const name of names) {
        const trimmed = name.trim();
        const key = normalizeCompanyName(trimmed);
        if (!key || seenContactNames.has(key) || isLikelyCompanyName(trimmed)) {
            continue;
        }
        if (
            savedNames.some((savedName) => isSamePartyName(trimmed, savedName))
        ) {
            continue;
        }
        seenContactNames.add(key);
        contactNames.push(trimmed);
    }
    return contactNames;
}

export function changedCounterpartyDetails({
    extracted,
    saved,
}: {
    extracted: ExtractedCounterpartyParty;
    saved: SavedCounterpartyDetails;
}): CounterpartyDetailChanges {
    const savedNames = [
        saved.name,
        extracted.partyName,
        ...saved.contactNames,
    ].flatMap((name) => (name ? [name] : []));
    const contactNames = personNamesNotOnRecord({
        names: extracted.names,
        savedNames,
    });

    return {
        emails: valuesMissingFromSaved({
            extracted: extracted.emails,
            savedKeys: new Set(
                saved.emails.flatMap((email) => {
                    const key = emailKey(email);
                    return key ? [key] : [];
                }),
            ),
            toKey: emailKey,
        }),
        phoneNumbers: valuesMissingFromSaved({
            extracted: extracted.phoneNumbers,
            savedKeys: new Set(
                saved.phoneNumbers.flatMap((phoneNumber) => {
                    const key = matchablePhoneDigits(phoneNumber);
                    return key ? [key] : [];
                }),
            ),
            toKey: matchablePhoneDigits,
        }),
        contactNames,
        addresses: addressesMissingFromSaved({
            extracted: extracted.addresses,
            saved: saved.addresses,
        }),
    };
}

export function hasCounterpartyDetailChanges(
    changes: CounterpartyDetailChanges,
): boolean {
    return (
        changes.emails.length > 0 ||
        changes.phoneNumbers.length > 0 ||
        changes.contactNames.length > 0 ||
        changes.addresses.length > 0
    );
}
